import React, { useEffect, useState } from 'react'
import { Alert } from 'react-bootstrap'
import { useParams } from 'react-router-dom'
import BigSpinner from 'common/components/BigSpinner'
import Title from 'common/components/Title'
import API from 'common/logic/API'
import Utils from 'common/Utils'
import { DataTable, ColumnDefinition } from 'common/components/DataTable'
import { AssetPoolBatch, AssetTransaction, FinancialAsset } from 'types'

function AssetPoolDetails() {
    const { id } = useParams()
    const [asset, setAsset] = useState<FinancialAsset | null>(null)
    const [batches, setBatches] = useState<AssetPoolBatch[] | null>(null)
    const [transactions, setTransactions] = useState<AssetTransaction[] | null>(null)
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        if (!id) {
            setError('Missing asset id')
            return
        }

        API.multiple({
            asset: API.get(`/financial-assets/${id}`),
            pool: API.get(`/financial-assets/${id}/pool`),
            transactions: API.get('/asset-transactions', { asset: id, sort: '-date', limit: 100000 }),
        }).then((response: any) => {
            setAsset(response.asset as FinancialAsset)
            setBatches((response.pool?.results ?? []) as AssetPoolBatch[])
            setTransactions((response.transactions?.results ?? []) as AssetTransaction[])
            setError(null)
        }).catch((err: any) => {
            setError(err?.response?.data?.message ?? err?.message ?? 'Failed to load asset pool')
        })
    }, [id])

    if (error) {
        return <><Title title="Asset pool" /><Alert variant="danger">{error}</Alert></>
    }

    if (asset === null || batches === null || transactions === null) {
        return <><Title title="Asset pool" /><BigSpinner /></>
    }

    const batchColumns = [
        new ColumnDefinition<AssetPoolBatch>('Date', 'date', 'td-size-s'),
        new ColumnDefinition<AssetPoolBatch>('Amount', obj => <>{Utils.formatPreciseAmount(obj.amount, true)}</>, 'td-currency', 'td-right'),
        new ColumnDefinition<AssetPoolBatch>('Average cost', obj => <>{Utils.formatPreciseAmount(obj.averageCost)} €</>, 'td-currency', 'td-right'),
        new ColumnDefinition<AssetPoolBatch>('Value', obj => <>{Utils.formatMoney(obj.value, true)} €</>, 'td-currency', 'td-right'),
        new ColumnDefinition<AssetPoolBatch>('Notes', obj => <small className="text-muted">{obj.notes}</small>)
    ]

    const txColumns = [
        new ColumnDefinition<AssetTransaction>('Date', 'date', 'td-size-s'),
        new ColumnDefinition<AssetTransaction>('Type', obj => obj.transactionType === 'BUY' ? 'Buy' : 'Sell', 'td-size-s'),
        new ColumnDefinition<AssetTransaction>('Asset amount', obj =>
            <>{Utils.formatPreciseAmount(obj.assetAmount, true)}</>, 'td-currency', 'td-right'
        ),
        new ColumnDefinition<AssetTransaction>('Euro amount', obj =>
            <>{Utils.formatMoney(obj.euroAmount, true)} €</>, 'td-currency', 'td-right'
        ),
        new ColumnDefinition<AssetTransaction>('Price per item', obj => {
            const assetAmount = Number(obj.assetAmount)
            const euroAmount = Number(obj.euroAmount)
            const pricePerItem = obj.pricePerItem == null ? null : Number(obj.pricePerItem)
            const computed = Number.isFinite(assetAmount) && Number.isFinite(euroAmount) && assetAmount !== 0
                ? Math.abs(euroAmount) / Math.abs(assetAmount)
                : NaN
            const value = Number.isFinite(pricePerItem as number) ? pricePerItem as number : computed
            return Number.isFinite(value)
                ? <>{Utils.formatPreciseAmount(value)} €</>
                : <span className="text-muted">—</span>
        }, 'td-currency', 'td-right'),
        new ColumnDefinition<AssetTransaction>('Realized gain', obj => {
            if (obj.transactionType === 'BUY') return <span className="text-muted">—</span>
            const value = Number(obj.realizedGain)
            const cls = value > 0 ? 'text-success' : value < 0 ? 'text-danger' : ''
            return <span className={cls}>{Utils.formatMoney(obj.realizedGain, true)} €</span>
        }, 'td-currency', 'td-right'),
        new ColumnDefinition<AssetTransaction>('Notes', obj =>
            <small className="text-muted">{obj.notes}</small>
        ),
    ]

    return <>
        <Title title={asset.name} subtitle={<small>{asset.fund.name}</small>} />
        <p>
            Amount: <strong>{Utils.formatPreciseAmount(asset.amountHeld)}</strong>{' '}
            | Average cost: <strong>{Number(asset.amountHeld) > 0 ? `${Utils.formatPreciseAmount(asset.averageCost)} €` : '—'}</strong>{' '}
            | Value: <strong>{Utils.formatMoney(asset.value)} €</strong>
        </p>
        <h2>Batches</h2>
        {batches.length === 0 ? <p>No batches are currently held for this asset.</p> : <DataTable columns={batchColumns} rows={batches} />}
        <h2 className="mt-4">Transactions</h2>
        {transactions.length === 0 ? <p>No transactions for this asset.</p> : <DataTable columns={txColumns} rows={transactions} />}
    </>
}

export default AssetPoolDetails
